// src/config/constants.ts
// Constantes globales, roles, planes, límites y tipos de negocio del ecosistema NIIZ ERP.

export const APP_CONFIG = {
    NAME: 'NIIZ Computing Platform',
    VERSION: '2.5.0',
    DEFAULT_CURRENCY: '$',
    DEFAULT_TAX_RATE: 0,
};

export const ROLES = {
    ADMIN: 'ADMIN',
    MANAGER: 'MANAGER',
    CASHIER: 'CASHIER',
    WAITER: 'WAITER',
    KITCHEN: 'KITCHEN',
    RECEPTION: 'RECEPTION',
    HOUSEKEEPING: 'HOUSEKEEPING',
    WAREHOUSE_MANAGER: 'WAREHOUSE_MANAGER',
    /**
     * 🆕 Rol preparado para Contabilidad Avanzada (contador del negocio).
     * Aún NO existe el flujo de alta de usuarios con este rol, por lo que
     * ningún perfil lo tiene asignado. Cuando quieras activarlo:
     *   1. Agrega 'ACCOUNTANT' a UserRole en src/store/authStore.ts
     *   2. En Sidebar.tsx: cambia `roles: ['ADMIN','MANAGER']` del item de
     *      Contabilidad por `roles: ['ADMIN','MANAGER','ACCOUNTANT']`.
     *   3. Listo — el AccountingRoleGuard ya valida contra ACCOUNTING_ROLES.
     */
    ACCOUNTANT: 'ACCOUNTANT',
} as const;

/**
 * Super roles con acceso IRRESTRICTO a la administración del negocio
 * (usuarios, config global, unidades, servicios, marketplace, etc.).
 * ⚠️ NO agregar ACCOUNTANT aquí: el contador solo debe ver Contabilidad.
 */
export const SUPER_ROLES = [ROLES.ADMIN, ROLES.MANAGER] as const;

/**
 * 🆕 Roles autorizados a entrar al módulo de Contabilidad Avanzada.
 * Hoy: ADMIN y MANAGER.
 * Preparado para incluir ACCOUNTANT sin tocar App.tsx ni Sidebar.tsx
 * (el guard `AccountingRoleGuard` valida contra esta constante).
 */
export const ACCOUNTING_ROLES = [ROLES.ADMIN, ROLES.MANAGER, ROLES.ACCOUNTANT] as const;

export const BUSINESS_TYPES = {
    RETAIL: 'RETAIL',
    RESTAURANT: 'RESTAURANT',
    PROPERTY: 'PROPERTY',
    RENTAL: 'RENTAL',
    HOSPITALITY: 'HOSPITALITY',
    /** 🆕 Logística, transporte y cadena de suministro. */
    LOGISTICS: 'LOGISTICS',
    /** 🥕 Zanahoria Studio: firmas contables que administran varios clientes. */
    ACCOUNTING_FIRM: 'ACCOUNTING_FIRM',
} as const;

// ─────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE PLANES Y MONETIZACIÓN (ACTUALIZADO 2025)
// ─────────────────────────────────────────────────────────────

export const PLANS = {
    FREE: 'FREE',
    NARANJA_PLUS: 'NARANJA_PLUS',
    ADVANCED_ACCOUNTING: 'ADVANCED_ACCOUNTING',
    /** 🆕 Plan para estudios contables / firmas (Zanahoria Studio). */
    STUDIO_FIRM: 'STUDIO_FIRM',
} as const;

export type PlanType = typeof PLANS[keyof typeof PLANS];

// Los precios de los planes viven en `src/config/pricing.ts` y en la tabla
// `public.plan_prices`. No se duplican aquí.

// ─────────────────────────────────────────────────────────────
// ENRUTAMIENTO POR DEFECTO
// ─────────────────────────────────────────────────────────────

export const ROUTES_BY_ROLE: Record<string, string> = {
    [ROLES.ADMIN]: '/platforms/retail/pos',
    [ROLES.MANAGER]: '/platforms/retail/pos',
    [ROLES.CASHIER]: '/platforms/retail/pos',
    [ROLES.WAITER]: '/platforms/restaurant/floor',
    [ROLES.KITCHEN]: '/platforms/restaurant/kds',
    [ROLES.RECEPTION]: '/platforms/hospitality/calendar',
    [ROLES.HOUSEKEEPING]: '/platforms/warehouse/rack',
    [ROLES.WAREHOUSE_MANAGER]: '/platforms/warehouse/rack',
    // 🆕 El contador aterriza en su módulo principal.
    [ROLES.ACCOUNTANT]: '/platforms/tools/contabilidad',
} as const;

export const ROUTES_BY_BUSINESS: Record<string, string> = {
    [BUSINESS_TYPES.RETAIL]: '/platforms/retail/pos',
    [BUSINESS_TYPES.RESTAURANT]: '/platforms/restaurant/floor',
    [BUSINESS_TYPES.PROPERTY]: '/platforms/warehouse/rack',
    [BUSINESS_TYPES.RENTAL]: '/platforms/booking/dashboard',
    [BUSINESS_TYPES.HOSPITALITY]: '/platforms/hospitality/calendar',
    /** 🆕 Dashboard de logística y flotas. */
    [BUSINESS_TYPES.LOGISTICS]: '/platforms/logistics/dashboard',
    [BUSINESS_TYPES.ACCOUNTING_FIRM]: '/platforms/accounting-firm/dashboard',
} as const;

/** 🆕 Ruta raíz del panel Zanahoria Studio (estudios contables). */
export const ROUTES_STUDIO = '/studio' as const;

// ─────────────────────────────────────────────────────────────
// 🆕 CONFIGURACIÓN INTERNACIONAL DE MONEDA Y FORMATO
// ─────────────────────────────────────────────────────────────

/**
 * Configuración de moneda por defecto de la plataforma.
 * Agnóstica: el negocio puede sobrescribir estos valores en settings.
 */
export const CURRENCY_CONFIG = {
    /** Código ISO 4217 por defecto. */
    DEFAULT_CODE: 'COP',
    /** Símbolo a mostrar en la UI. */
    DEFAULT_SYMBOL: '$',
    /** Separador decimal. */
    DEFAULT_DECIMAL_SEPARATOR: ',',
    /** Separador de miles. */
    DEFAULT_THOUSAND_SEPARATOR: '.',
    /** Dígitos decimales estándar. */
    DEFAULT_FRACTION_DIGITS: 2,
} as const;

/**
 * Jurisdicciones fiscales soportadas por el motor financiero.
 * El patrón estrategia de `taxPayroll.ts` consume este valor.
 */
export const TAX_JURISDICTIONS = {
    /** Estatuto Tributario colombiano (DIAN, Art. 114-1, ReteFuente, ReteICA). */
    COLOMBIA_DIAN: 'COLOMBIA_DIAN',
    /** Genérico para México, Perú, España, USA y cualquier otro país. */
    INTERNATIONAL_STANDARD: 'INTERNATIONAL_STANDARD',
} as const;

export type TaxJurisdiction = typeof TAX_JURISDICTIONS[keyof typeof TAX_JURISDICTIONS];

// ─────────────────────────────────────────────────────────────
// 🆕 CAJAS MAESTRAS NORMATIVAS (REGLA 50/20/15/15)
// ─────────────────────────────────────────────────────────────

/**
 * Porcentajes normativos de distribución automática del flujo neto.
 * La suma SIEMPRE debe ser 100%.
 */
export const DEFAULT_CASH_BOX_PERCENTAGES = {
    OPERACION: 50,
    REINVERSION: 20,
    IMPUESTOS: 15,
    GANANCIAS: 15,
} as const;

/**
 * Jerarquía de donación para el rebalanceo automático de cajas deficitarias.
 * IMPUESTOS NUNCA aparece aquí: es sagrado e intocable por regla, no por nombre.
 *
 *   1. REINVERSION  (Buffer primario: absorbe shocks de caja)
 *   2. GANANCIAS    (Reserva de valor: donante secundario)
 *   3. OPERACION    (Alta rotación: último recurso)
 */
export const DONOR_BOX_HIERARCHY = ['REINVERSION', 'GANANCIAS', 'OPERACION'] as const;

/** Tipos de caja administrativa. */
export const CASH_BOX_ROLES = {
    OPERATING: 'OPERATING',
    REINVESTMENT: 'REINVESTMENT',
    TAX: 'TAX',
    PROFIT: 'PROFIT',
    CUSTOM: 'CUSTOM',
} as const;

// ─────────────────────────────────────────────────────────────
// 🆕 CONFIGURACIÓN DE SUCURSALES (BRANCHES)
// ─────────────────────────────────────────────────────────────

/**
 * Sede principal por defecto que se crea en la auto-sanación v109.
 * Siempre existe al menos una sede (`is_main: true`).
 */
export const DEFAULT_BRANCH = {
    id: 'main',
    code: 'MAIN',
    name: 'Sede Principal / Global',
    is_main: true,
    is_active: true,
} as const;

/**
 * Límite de sucursales permitidas por plan.
 * Usado por el guard de creación de branches.
 */
export const BRANCH_LIMITS_BY_PLAN: Record<PlanType, number> = {
    [PLANS.FREE]: 1,
    [PLANS.NARANJA_PLUS]: 3,
    [PLANS.ADVANCED_ACCOUNTING]: 10,
    [PLANS.STUDIO_FIRM]: 50,
} as const;

// ─────────────────────────────────────────────────────────────
// 🆕 FACTURACIÓN DIAN Y CÓDIGOS CORTOS
// ─────────────────────────────────────────────────────────────

/**
 * Configuración por defecto de la resolución de facturación electrónica.
 */
export const INVOICE_CONFIG = {
    /** Prefijo por defecto de las facturas. */
    defaultPrefix: 'FAC',
    /** Longitud del padding numérico (FAC-0001 → 4 dígitos). */
    paddingLength: 4,
    /** Inicio del rango autorizado por la DIAN (primera resolución). */
    initialRangeFrom: 1,
    /** Fin del rango autorizado por la DIAN (primera resolución). */
    initialRangeTo: 10000,
    /** Días antes del vencimiento para alertar al usuario. */
    resolutionAlertThresholdDays: 30,
} as const;

// ─────────────────────────────────────────────────────────────
// 🆕 ZANAHORIA STUDIO & SCOPES DE API KEY
// ─────────────────────────────────────────────────────────────

/**
 * Scopes de permisos para API Keys emitidas por el plan STUDIO_FIRM.
 * Usados por el middleware de autenticación de la API pública.
 */
export const API_KEY_SCOPES = {
    /** Lectura del snapshot financiero universal. */
    READ_FINANCIAL_MATRIX: 'read:financial_matrix',
    /** Ajustes manuales de impuestos (contadores auditores). */
    WRITE_TAX_ADJUSTMENTS: 'write:tax_adjustments',
    /** Descarga de documentos y certificados. */
    READ_DOCUMENTS: 'read:documents',
    /** Bloqueo explícito de operaciones POS (solo lectura contable). */
    DENY_POS_OPERATIONS: 'deny:pos_operations',
} as const;

export type ApiKeyScope = typeof API_KEY_SCOPES[keyof typeof API_KEY_SCOPES];

// ─────────────────────────────────────────────────────────────
// 🆕 PROGRAMA DE AFILIADOS (REFERIDOS)
// ─────────────────────────────────────────────────────────────

/**
 * Reglas del programa de referidos para contadores y merchants.
 * La comisión se calcula sobre la GANANCIA NETA generada (no sobre el ingreso).
 */
export const AFFILIATE_CONFIG = {
    /** 10.00% de la ganancia neta generada por el referido (en basis points). */
    defaultCommissionBps: 1000,
    /** Monto mínimo acumulado para solicitar un payout en COP. */
    minPayoutCop: 50000,
    /** Tipos de afiliado soportados. */
    referrerTypes: ['ACCOUNTANT', 'MERCHANT', 'USER'] as const,
    /** Días de gracia antes de liberar la comisión (anti-fraude). */
    holdingPeriodDays: 30,
} as const;

// ─────────────────────────────────────────────────────────────
// 🆕 ENSAMBLAJE Y PRODUCCIÓN (BOM / BILL OF MATERIALS)
// ─────────────────────────────────────────────────────────────

/**
 * Configuración del motor de órdenes de producción.
 */
export const PRODUCTION_CONFIG = {
    /** Límite técnico de materias primas por orden de ensamblaje. */
    maxRawMaterialsPerAssembly: 50,
    /** Merma técnica normal por defecto (2.0%). Se absorbe en el costo. */
    defaultNormalShrinkagePct: 2.0,
    /** Umbral para clasificar una merma como ANORMAL (pasa a P&G). */
    abnormalShrinkageThresholdPct: 5.0,
    /** Estados de una orden de producción. */
    statuses: ['DRAFT', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const,
} as const;

// ─────────────────────────────────────────────────────────────
// 🆕 ESTADOS DE SALUD DE CAJAS Y ALERTAS
// ─────────────────────────────────────────────────────────────

export const BOX_HEALTH_STATUS = {
    HEALTHY: 'HEALTHY',
    WARNING: 'WARNING',
    CRITICAL: 'CRITICAL',
} as const;

export const TAX_COVERAGE_STATUS = {
    /** CR ≥ 1.0: pasivo cubierto. */
    HEALTHY: 'HEALTHY',
    /** 0.85 ≤ CR < 1.0: aproximación al piso. */
    WARNING: 'WARNING',
    /** CR < 0.85: déficit tributario. */
    CRITICAL: 'CRITICAL',
} as const;

export const FCCR_STATUS = {
    /** FCCR < 1.0: quema de caja crítica. */
    ROJO_AHOGO: 'ROJO_AHOGO',
    /** 1.0 ≤ FCCR < 1.3: sin colchón de seguridad. */
    AMARILLO_ALERTA: 'AMARILLO_ALERTA',
    /** FCCR ≥ 1.3: operación saludable. */
    VERDE_SALUDABLE: 'VERDE_SALUDABLE',
} as const;